import { ROUTES } from "../constants/routes";
import {
  invalidEntryTypeMessage,
  slipAmountEmptyMessage,
  loginToPlaceMessage,
  teaserMoneylineMessage,
  teaserMoneylineSelectionsMessage,
  entryPlacedMessage,
  oddsChangedMessage,
} from "../utility/entryCopy";
import {
  applyUpdatedLegsToSlip,
  isOddsChangedResponse,
  recomputeParlaySlipPayout,
} from "../utility/acceptOddsChanges";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { SET_CHECK_IND } from "../components/constants";
import {
  addOrder,
  getPointGames,
  getWalletDetails,
} from "../components/actions/gameSettings";
import {
  normalizeBetsForSubmit,
  applyCallBetValidationToLeg,
  sanitizeDesiredLineInput,
  sanitizeDesiredOddsInput,
} from "../components/lib/betSlipBuyPoints";
import {
  buildBuyPointSelectOptions,
  isBuyPointsAdminEnabled,
  resolveBuyPointsForSlipItem,
  validateBuyPointsSlipBeforeSubmit,
} from "../utility/buyPointOptions";
import {
  calcParlayCombinedAmericanOdds,
  calcParlayPayout,
  formatAmericanOdds,
  getLegPricesFromSelections,
  PARLAY_MIN_LEGS,
} from "../utility/parlayOdds";
import {
  getParlayLegsFromSubmitData,
  resolveOrderSubmitErrorMessage,
  summarizeTicketValidationErrors,
  validateParlayBeforeSubmit,
} from "../utility/parlaySubmit";
import { sameEventComboMessage } from "../utility/entryCopy";
import {
  isSameEventRestrictedTab,
  sanitizeLegsForParlaySameEvent,
  wouldCreateSameEventCombo,
} from "../utility/sameEventCombo";
import {
  roundMoney,
  calcWinFromRisk,
  calcRiskFromWin,
} from "../components/lib/wagerFinancials";
import { recalculateCallBetPayout } from "../utility/callBetDisplay";
import {
  calculateTeaserPayout,
  getAllowedTeaserPoints,
  resolveTeaserChartForLegs,
  sanitizeLegsForTeaser,
  adjustTeaserLine,
} from "../utility/teaserOdds";
import { toastAlert } from "../components/lib/toastAlert";
import { isEmpty } from "../components/lib/isEmpty";
import { WALLET_REFRESH_EVENT } from "../utility/walletRefresh";
import { isDTS } from "../utility/platform";
import {
  addFundingBucket,
  getCoinModeBalance,
  useCoinMode,
} from "./CoinModeContext";
import { BET_SLIP_INDEX, buildSlipItemFromGameOutcome } from "../utility/betSlipSelection";

const MAX_BET_DIGITS = 7;
const MAX_BET_AMOUNT = 1000000;

export const OPEN_BETS_REFRESH_EVENT = "dreamsports:open-bets-refresh";

const BetSlipContext = createContext(null);

export function useBetSlip() {
  const ctx = useContext(BetSlipContext);
  if (!ctx) {
    throw new Error("useBetSlip must be used within BetSlipProvider");
  }
  return ctx;
}

/** Optional hook — returns null outside provider (for shared components). */
export function useBetSlipOptional() {
  return useContext(BetSlipContext);
}

export function BetSlipProvider({ children, stayOnPageAfterSubmit = false }) {
  const dispatch = useDispatch();
  const history = useHistory();
  const accountData = useSelector((state) => state.account);
  const { coinMode } = useCoinMode();
  const reduxCheckInd = useSelector(
    (state) => state.checkIndReducer.checkInd
  ) || [];

  const [betTabName, setBetTabName] = useState("Straight Bet"); // compliance-ok: tab id, labels go through getEntryTypeLabel
  const [CheckInd, setCheckInd] = useState(reduxCheckInd);
  const [walletData, setwalletData] = useState([]);
  const [buyPointsMap, setBuyPointsMap] = useState({});
  const [totalBet, setTotalBet] = useState("");
  const [totalTeaserBet, settotalTeaserBet] = useState("");
  const [teaserPoints, setTeaserPoints] = useState(6);
  const [callBetListRefreshKey, setCallBetListRefreshKey] = useState(0);
  const [acceptOddsChanges, setAcceptOddsChanges] = useState(false);
  const prevBetTabRef = useRef("Straight Bet"); // compliance-ok: tab id

  const isLoggedIn = Boolean(accountData?.emailId);

  useEffect(() => {
    dispatch({ type: SET_CHECK_IND, data: CheckInd });
  }, [CheckInd, dispatch]);

  const fetchWalletData = useCallback(async () => {
    if (!accountData?.emailId) return;
    try {
      const { status, result } = await getWalletDetails({
        email: accountData.emailId,
      });
      if (status === true) setwalletData(result);
    } catch (err) {
      console.log("fetchWalletData", err);
    }
  }, [accountData?.emailId]);

  useEffect(() => {
    if (accountData?.emailId) fetchWalletData();
  }, [accountData?.emailId, fetchWalletData]);

  useEffect(() => {
    const onWalletRefresh = () => fetchWalletData();
    window.addEventListener(WALLET_REFRESH_EVENT, onWalletRefresh);
    return () =>
      window.removeEventListener(WALLET_REFRESH_EVENT, onWalletRefresh);
  }, [fetchWalletData]);

  const fetchPointGames = useCallback(async (gameTitle, slipItem) => {
    try {
      const { status, result } = await getPointGames({ title: gameTitle });
      return resolveBuyPointsForSlipItem(status, result, gameTitle, slipItem);
    } catch {
      return false;
    }
  }, []);

  useEffect(() => {
    const legs = CheckInd?.[BET_SLIP_INDEX] || [];
    const titles = [
      ...new Set(legs.map((item) => item?.sport_title).filter(Boolean)),
    ];
    if (!titles.length) {
      setBuyPointsMap({});
      return;
    }

    let cancelled = false;
    (async () => {
      const next = {};
      await Promise.all(
        titles.map(async (title) => {
          try {
            const { status, result } = await getPointGames({ title });
            next[String(title).trim().toLowerCase()] = isBuyPointsAdminEnabled(
              status,
              result,
              title
            );
          } catch {
            next[String(title).trim().toLowerCase()] = false;
          }
        })
      );
      if (!cancelled) setBuyPointsMap(next);
    })();

    return () => {
      cancelled = true;
    };
  }, [CheckInd]);

  const resetBetSlip = useCallback(() => {
    const empty = [[]];
    setCheckInd(empty);
    dispatch({ type: SET_CHECK_IND, data: empty });
    setTotalBet("");
    settotalTeaserBet("");
    setAcceptOddsChanges(false);
  }, [dispatch]);

  const refreshSlipFromOddsChanged = useCallback(
    (updatedLegs, type) => {
      setCheckInd((prev) => {
        const current = Array.isArray(prev?.[BET_SLIP_INDEX])
          ? prev[BET_SLIP_INDEX]
          : [];
        let { legs } = applyUpdatedLegsToSlip(current, updatedLegs);
        if (type === "parlay") {
          legs = recomputeParlaySlipPayout(legs, totalBet);
        }
        const next = [...(prev || [])];
        next[BET_SLIP_INDEX] = legs;
        return next;
      });
    },
    [totalBet]
  );

  const teamsSelect = useCallback(
    async (e, item) => {
      if (e.target.checked) {
        const currentSlip = CheckInd?.[BET_SLIP_INDEX] || [];

        if (
          isSameEventRestrictedTab(betTabName) &&
          wouldCreateSameEventCombo(currentSlip, item)
        ) {
          e.target.checked = false;
          toastAlert("error", sameEventComboMessage(), "same-event-combo");
          return;
        }

        if (betTabName === "Teaser") {
          const chartCheck = resolveTeaserChartForLegs([...currentSlip, item]);
          if (!chartCheck.ok) {
            e.target.checked = false;
            toastAlert("error", chartCheck.message, "teaser-validation");
            return;
          }
        }

        const buyPointsEnabled = await fetchPointGames(
          item.sport_title,
          item
        );
        setCheckInd((prev) => {
          const next = JSON.parse(JSON.stringify(prev));
          let checkArr = next[BET_SLIP_INDEX] || [];
          const filteredArr = checkArr.filter(
            (val) => !(val.id === item.id && val.type === item.type)
          );

          let nextItem = {
            ...item,
            buypoint: buyPointsEnabled,
            originalPoint: item.point,
            originalPrice: item.price,
            desiredPoint: "",
            desiredPrice: "",
            isCallBetType: "false",
          };

          if (betTabName === "Teaser") {
            const seedBuyPoints = Number(teaserPoints);
            const seedAdjusted = adjustTeaserLine(
              { ...item, buyPoints: seedBuyPoints },
              seedBuyPoints
            );
            nextItem = {
              ...nextItem,
              buyPoints: seedBuyPoints,
              adjustedPoint: seedAdjusted,
            };
          } else {
            nextItem = { ...nextItem, buyPoints: 0 };
          }

          filteredArr.push(nextItem);
          next[BET_SLIP_INDEX] = filteredArr;
          return next;
        });
      } else {
        setCheckInd((prev) => {
          const next = JSON.parse(JSON.stringify(prev));
          let checkArr = next[BET_SLIP_INDEX] || [];
          const fidIndxx = checkArr.findIndex((val) =>
            item.CheckId
              ? val.CheckId === item.CheckId
              : val.id === item.id &&
                val.type === item.type &&
                val.teamName === item.teamName
          );
          if (fidIndxx !== -1) {
            checkArr.splice(fidIndxx, 1);
            next[BET_SLIP_INDEX] = checkArr;
            return next;
          }
          return prev;
        });
      }
    },
    [betTabName, CheckInd, fetchPointGames, teaserPoints]
  );

  const handleGameSelection = useCallback(
    (e, game, marketKey, outcomeIndex) => {
      if (e.target.checked && betTabName === "Teaser" && marketKey === "h2h") {
        e.target.checked = false;
        toastAlert(
          "error",
          teaserMoneylineSelectionsMessage()
        );
        return;
      }

      const teamObj = buildSlipItemFromGameOutcome(
        game,
        marketKey,
        outcomeIndex
      );
      if (!teamObj) {
        e.target.checked = false;
        return;
      }
      teamsSelect(e, teamObj);
    },
    [betTabName, teamsSelect]
  );

  const isSelectionChecked = useCallback(
    (checkId, gameId) => {
      const slip = CheckInd?.[BET_SLIP_INDEX];
      if (!Array.isArray(slip)) return false;
      return slip.some(
        (item) => item.CheckId === checkId && item.id === gameId
      );
    },
    [CheckInd]
  );

  const handleRemoveTab = useCallback(
    (item, index) => {
      const newAdd = [...(CheckInd[BET_SLIP_INDEX] || [])];
      newAdd.splice(index, 1);
      setCheckInd([newAdd]);
      dispatch({ type: SET_CHECK_IND, data: [newAdd] });
    },
    [CheckInd, dispatch]
  );

  const handleNumberInput = (value, callback) => {
    if (!/^\d*$/.test(value)) return;
    if (value.length > MAX_BET_DIGITS) return;
    if (value !== "" && Number(value) > MAX_BET_AMOUNT) return;
    callback(value);
  };

  const straightpayOutCalc = (value, price, groupIdx, itemIdx, mode) => {
    const amount = Number(value) || 0;
    setCheckInd((prev) => {
      const next = [...prev];
      const group = [...(next[groupIdx] || [])];
      const item = { ...(group[itemIdx] || {}) };
      const payoutPrice = item.desiredPrice || price;

      let risk = 0;
      let win = 0;
      if (mode === "risk") {
        risk = amount;
        win = calcWinFromRisk(risk, payoutPrice);
      }
      if (mode === "win") {
        win = amount;
        risk = calcRiskFromWin(win, payoutPrice);
      }

      risk = roundMoney(risk);
      win = roundMoney(win);
      const payout = roundMoney(risk + win);

      item.activatedTab = "straight";
      item.Price = risk;
      item.risk = risk;
      item.win = win;
      item.payout = payout;

      group[itemIdx] = item;
      next[groupIdx] = group;
      return next;
    });
  };

  const handlePoints = (value, index, ind) => {
    const nextSlip = [...CheckInd];
    const row = { ...(nextSlip[ind][index] || {}) };
    const base = Number(row.originalPoint ?? row.point);

    row.point = value.point;
    row.price = value.price;
    row.payout = roundMoney(
      parseFloat(row.risk || 0) + parseFloat(row.win || 0)
    );
    row.customizedPoint = value.point;
    row.isCallBetType = "false";

    if (Number.isFinite(base) && Number.isFinite(Number(value.point))) {
      row.buyPoints =
        Math.round(Math.abs(Number(value.point) - base) * 100) / 100;
    } else {
      row.buyPoints = 0;
    }

    nextSlip[ind][index] = row;
    setCheckInd(nextSlip);
  };

  const updateDesiredLine = (value, groupIdx, itemIdx) => {
    const sanitized = sanitizeDesiredLineInput(value);
    setCheckInd((prev) => {
      const next = [...prev];
      const group = [...(next[groupIdx] || [])];
      const item = { ...(group[itemIdx] || {}) };
      item.desiredPoint = sanitized;
      item.activatedTab = "straight";
      item.customizedPoint = item.originalPoint ?? item.point;
      item.point = item.originalPoint ?? item.point;
      item.buyPoints = 0;
      group[itemIdx] = applyCallBetValidationToLeg(item);
      next[groupIdx] = group;
      return next;
    });
  };

  const updateDesiredPrice = (value, groupIdx, itemIdx) => {
    const sanitized = sanitizeDesiredOddsInput(value);
    setCheckInd((prev) => {
      const next = [...prev];
      const group = [...(next[groupIdx] || [])];
      const item = { ...(group[itemIdx] || {}) };
      item.desiredPrice = sanitized;
      item.activatedTab = "straight";
      item.buyPoints = 0;
      group[itemIdx] = recalculateCallBetPayout(applyCallBetValidationToLeg(item));
      next[groupIdx] = group;
      return next;
    });
  };

  const handleParlayChange = (value) => {
    const sanitized = String(value ?? "").replace(/[^\d.]/g, "");
    if (sanitized.length > MAX_BET_DIGITS + 3) return;
    const numeric = Number(sanitized);
    if (Number.isFinite(numeric) && numeric > MAX_BET_AMOUNT) return;
    const bet = Number.isFinite(numeric) ? numeric : 0;
    setTotalBet(sanitized === "" ? "" : bet);

    setCheckInd((prev) => {
      const next = [...prev];
      const group = [...(next[0] || [])];
      if (!group.length) return next;

      const legPrices = getLegPricesFromSelections(group);
      if (legPrices.length !== group.length) {
        next[0] = group;
        return next;
      }

      const { win: parleyWin, payout } = calcParlayPayout(bet, legPrices);
      const updatedGroup = group.map((item) => ({
        ...item,
        activatedTab: "parlay",
        risk: roundMoney(bet),
        Price: roundMoney(bet),
        win: roundMoney(parleyWin),
        payout: roundMoney(payout),
      }));

      next[0] = updatedGroup;
      return next;
    });
  };

  const updateAllTeaserItems = (betValue, teamCount, buyPoints) => {
    settotalTeaserBet(betValue === "" ? "" : betValue);
    if (!teamCount || teamCount < 2) return;

    setCheckInd((prev) => {
      const next = (Array.isArray(prev) ? prev : []).map((group) => {
        if (!Array.isArray(group)) return group;
        return group.map((item) => {
          if (item.type === "h2h") return item;
          const payout = calculateTeaserPayout(
            Number(betValue) || 0,
            teamCount,
            buyPoints
          );
          const adjustedPoint = adjustTeaserLine(
            { ...item, buyPoints },
            buyPoints
          );
          return {
            ...item,
            activatedTab: "teaser",
            Price: betValue,
            payout,
            win: roundMoney(Number(payout) - Number(betValue)),
            risk: betValue,
            buyPoints,
            originalPoint: item.originalPoint ?? item.point,
            adjustedPoint,
          };
        });
      });
      return next;
    });
  };

  const parlaySlip = CheckInd?.[BET_SLIP_INDEX] || [];
  const parlayLegPriceKey = parlaySlip
    .map((l) => `${l?.id}-${l?.price}-${l?.point}`)
    .join("|");

  const parlayLegPrices = useMemo(
    () => getLegPricesFromSelections(CheckInd?.[BET_SLIP_INDEX] || []),
    // parlayLegPriceKey tracks leg price/point changes without unstable [] fallback refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [parlayLegPriceKey]
  );

  const parlayCombinedOdds = useMemo(
    () => calcParlayCombinedAmericanOdds(parlayLegPrices),
    [parlayLegPrices]
  );

  const parlayHasValidLegPrices =
    parlaySlip.length > 0 && parlayLegPrices.length === parlaySlip.length;

  const parlayDisplayPayout = useMemo(() => {
    const bet = Number(totalBet);
    if (!Number.isFinite(bet) || bet <= 0 || !parlayHasValidLegPrices) return 0;
    return roundMoney(calcParlayPayout(bet, parlayLegPrices).payout);
  }, [totalBet, parlayLegPrices, parlayHasValidLegPrices]);

  useEffect(() => {
    if (betTabName !== "Parlay Bet") return;
    if (totalBet === "" || totalBet === null || totalBet === undefined) return;
    const bet = Number(totalBet);
    if (!Number.isFinite(bet) || bet <= 0) return;
    handleParlayChange(String(totalBet));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parlayLegPriceKey, betTabName]);

  useEffect(() => {
    if (betTabName === "Teaser" && CheckInd?.[0]?.length >= 2) {
      updateAllTeaserItems(totalTeaserBet, CheckInd[0].length, teaserPoints);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [betTabName, CheckInd?.[0]?.length]);

  const teaserChartResult = resolveTeaserChartForLegs(CheckInd?.[0] || []);
  const teaserChartKey = teaserChartResult.ok ? teaserChartResult.chartKey : null;
  const teaserSlipLength = CheckInd?.[0]?.length ?? 0;
  const supportedTeaserPoints = useMemo(
    () =>
      teaserChartKey
        ? getAllowedTeaserPoints(teaserChartKey, teaserSlipLength)
        : [],
    [teaserChartKey, teaserSlipLength]
  );

  const calcTeaserPayout = useCallback(
    (betAmount, teamCount, buyPoints = 6) => {
      if (!teaserChartKey) return 0;
      return roundMoney(
        calculateTeaserPayout(betAmount, teamCount, buyPoints, teaserChartKey)
      );
    },
    [teaserChartKey]
  );

  useEffect(() => {
    if (betTabName !== "Teaser") return;
    if (
      supportedTeaserPoints.length > 0 &&
      !supportedTeaserPoints.includes(teaserPoints)
    ) {
      const nextTeaserPoints = supportedTeaserPoints[0];
      setTeaserPoints(nextTeaserPoints);
      if (CheckInd?.[0]?.length > 1) {
        updateAllTeaserItems(
          totalTeaserBet,
          CheckInd[0].length,
          nextTeaserPoints
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [betTabName, supportedTeaserPoints, teaserPoints, CheckInd, totalTeaserBet]);

  useEffect(() => {
    const prevTab = prevBetTabRef.current;
    const switchedToTeaser = betTabName === "Teaser" && prevTab !== "Teaser";
    const switchedToParlay =
      betTabName === "Parlay Bet" && prevTab !== "Parlay Bet";
    prevBetTabRef.current = betTabName;

    const currentSlip = Array.isArray(CheckInd?.[0]) ? CheckInd[0] : [];
    if (!currentSlip.length) return;

    if (switchedToTeaser) {
      const { legs, changed, message } = sanitizeLegsForTeaser(currentSlip);
      if (!changed) return;
      setCheckInd((prev) => {
        const next = JSON.parse(JSON.stringify(prev));
        next[0] = legs;
        return next;
      });
      if (message) {
        toastAlert("error", message, "teaser-validation");
      }
      return;
    }

    if (switchedToParlay) {
      const { legs, changed, message } =
        sanitizeLegsForParlaySameEvent(currentSlip);
      if (!changed) return;
      setCheckInd((prev) => {
        const next = JSON.parse(JSON.stringify(prev));
        next[0] = legs;
        return next;
      });
      if (message) {
        toastAlert("error", message, "same-event-combo");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [betTabName]);

  const ticketValidaion = async (data, type) => {
    const errors = {};
    const validTypes = ["teaser", "straight", "parlay"];
    if (!validTypes.includes(type)) {
      errors.type = invalidEntryTypeMessage(type);
      return errors;
    }

    if (type === "straight") {
      const datas = [];
      for (let i = 0; i < data.length; i++) {
        if (isEmpty(data[i]?.Price) || Number(data[i]?.Price) <= 0) {
          datas[i] = { Price: slipAmountEmptyMessage() };
          errors.data = datas;
        }
      }
    }

    return Object.keys(errors).length ? errors : null;
  };

  const addNewOrder = async (data, type) => {
    try {
      if (!isLoggedIn) {
        toastAlert("error", loginToPlaceMessage());
        history.push("/login");
        return;
      }

      const wallet = walletData?.walletData || {};
      const availableBalance = isDTS()
        ? getCoinModeBalance(wallet, coinMode)
        : Number(wallet.userBalance || 0);
      let totalRequired = 0;

      if (type === "straight") {
        totalRequired = data.reduce(
          (sum, item) => sum + (Number(item.Price) || 0),
          0
        );
      } else if (type === "parlay") {
        totalRequired = Number(totalBet) || 0;
      } else if (type === "teaser") {
        totalRequired = Number(totalTeaserBet) || 0;
      }

      if (totalRequired > availableBalance) {
        toastAlert(
          "error",
          "Insufficient balance. Redirecting to wallet to add funds..."
        );
        setTimeout(() => history.push("/wallet"), 2000);
        return;
      }

      if (type === "parlay") {
        const parlayCheck = validateParlayBeforeSubmit({
          legs: getParlayLegsFromSubmitData(data),
          stake: totalBet,
        });
        if (!parlayCheck.ok) {
          toastAlert("error", parlayCheck.message);
          return;
        }
      }

      if (type === "teaser") {
        if (isEmpty(totalTeaserBet) || Number(totalTeaserBet) <= 0) {
          toastAlert("error", "Please enter a valid teaser stake");
          return;
        }
        const chartCheck = resolveTeaserChartForLegs(data);
        if (!chartCheck.ok) {
          toastAlert("error", chartCheck.message);
          return;
        }
        if (data.some((item) => item.type === "h2h")) {
          toastAlert("error", teaserMoneylineMessage());
          return;
        }
      }

      let formattedData = data;
      if (type === "straight" || type === "parlay") {
        formattedData = normalizeBetsForSubmit(data, type);
        const buyPointsCheck = validateBuyPointsSlipBeforeSubmit(
          formattedData,
          betTabName
        );
        if (!buyPointsCheck.ok) {
          toastAlert("error", buyPointsCheck.message);
          return;
        }
        if (type === "parlay") {
          const parlayCheck = validateParlayBeforeSubmit({
            legs: getParlayLegsFromSubmitData(formattedData),
            stake: totalBet,
          });
          if (!parlayCheck.ok) {
            toastAlert("error", parlayCheck.message);
            return;
          }
        }
      }

      const vald = await ticketValidaion(formattedData, type);
      if (!isEmpty(vald)) {
        const summary = summarizeTicketValidationErrors(vald);
        if (summary) toastAlert("error", summary);
        return;
      }

      const coinAwareData = isDTS() ? addFundingBucket(formattedData, coinMode) : formattedData;
      const orderResponse = await addOrder({
        data: coinAwareData,
        betType: betTabName,
        email: accountData?.emailId,
        openSpots: undefined,
        acceptOddsChanges,
      });
      const { status, message, code, updatedLegs } = orderResponse;

      if (status === true) {
        resetBetSlip();
        const callBetOnly =
          type === "straight" &&
          formattedData.length > 0 &&
          formattedData.every(
            (item) =>
              item.isCallBetType === true || item.isCallBetType === "true"
          );
        if (callBetOnly) {
          setBetTabName("Call Bets"); // compliance-ok: tab id
          setCallBetListRefreshKey((k) => k + 1);
        }
        toastAlert("success", entryPlacedMessage());
        if (stayOnPageAfterSubmit) {
          window.dispatchEvent(new CustomEvent(OPEN_BETS_REFRESH_EVENT));
        } else {
          history.push(ROUTES.openBet);
        }
      } else if (isOddsChangedResponse({ status, code, message })) {
        refreshSlipFromOddsChanged(updatedLegs, type);
        toastAlert("error", message || oddsChangedMessage(), "odds-changed");
      } else {
        toastAlert("error", resolveOrderSubmitErrorMessage({ message }, null));
      }
    } catch (err) {
      toastAlert("error", resolveOrderSubmitErrorMessage(null, err));
    }
  };

  const allItems = CheckInd?.flat() || [];
  const totalRisk = allItems.reduce(
    (sum, item) => sum + (parseFloat(item.risk) || 0),
    0
  );
  const totalWin = allItems.reduce(
    (sum, item) => sum + (parseFloat(item.win) || 0),
    0
  );

  const value = {
    betTabName,
    setBetTabName,
    CheckInd,
    buyPointsMap,
    totalBet,
    totalTeaserBet,
    teaserPoints,
    setTeaserPoints,
    supportedTeaserPoints,
    totalRisk,
    totalWin,
    parlayCombinedOdds,
    parlayHasValidLegPrices,
    parlayDisplayPayout,
    handleGameSelection,
    isSelectionChecked,
    handleRemoveTab,
    handleNumberInput,
    straightpayOutCalc,
    handlePoints,
    handleParlayChange,
    updateAllTeaserItems,
    addNewOrder,
    buildBuyPointSelectOptions,
    updateDesiredLine,
    updateDesiredPrice,
    callBetListRefreshKey,
    acceptOddsChanges,
    setAcceptOddsChanges,
    MAX_BET_DIGITS,
    MAX_BET_AMOUNT,
    BET_SLIP_INDEX,
    teaserChartResult,
    teaserChartKey,
    calcTeaserPayout,
    settotalTeaserBet,
  };

  return (
    <BetSlipContext.Provider value={value}>{children}</BetSlipContext.Provider>
  );
}

export { formatAmericanOdds, PARLAY_MIN_LEGS };
